// APB Command Center — client entry.
//
// 0. Sign-in gate (estimator /sso handoff) + app switcher.
// 1. Bootstrap the sync layer (hydrates localStorage from the API, then
//    installs a write-through interceptor for cross-device sync).
// 2. The rest of this file is the v0.8 mockup script — kept as-is so the
//    port is a straight re-host. All state still flows through localStorage,
//    which now double-writes to D1 in the background.
import { gate } from './auth.js';
import { initAppSwitch } from './appSwitch.js';
import { init as bootstrap } from './bootstrap.js';
await gate();          // resolves only when signed in (spec §3)
initAppSwitch();
await bootstrap();

// ============================================================================
// v0.8 mockup script — verbatim from command-center.html
// ============================================================================

  // ==================================================
  // LIVE CLOCK + DATE
  // ==================================================
  const clockEl = document.getElementById('clock');
  const dateEl = document.getElementById('date');
  const days = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  function tick() {
    const now = new Date();
    clockEl.textContent = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
    dateEl.textContent = `${days[now.getDay()]} · ${String(now.getDate()).padStart(2,'0')} ${months[now.getMonth()]} ${now.getFullYear()}`;
  }
  tick();
  setInterval(tick, 1000);

  // ==================================================
  // TIME-OF-DAY GREETING
  // ==================================================
  (function setGreeting() {
    const greetEl = document.getElementById('greeting');
    const hr = new Date().getHours();
    let phrase;
    if (hr < 5) phrase = 'STILL UP?';
    else if (hr < 12) phrase = 'GOOD MORNING,';
    else if (hr < 17) phrase = 'GOOD AFTERNOON,';
    else if (hr < 21) phrase = 'GOOD EVENING,';
    else phrase = 'LATE SHIFT,';
    greetEl.innerHTML = `<span class="sys">// ${phrase}</span>SUPERIOR ONE.`;
  })();

  // ==================================================
  // TOAST
  // ==================================================
  const toastEl = document.getElementById('toast');
  let toastT;
  function showToast(msg) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastT);
    toastT = setTimeout(() => toastEl.classList.remove('show'), 2600);
  }
  document.querySelectorAll('[data-toast]').forEach(btn => {
    btn.addEventListener('click', (e) => { e.stopPropagation(); showToast(btn.dataset.toast); });
  });
  document.querySelectorAll('[data-ext]').forEach(el => {
    el.addEventListener('click', (e) => {
      if (e.target.closest('a')) e.preventDefault();
      showToast(`Opens: ${el.dataset.ext} (Cloudflare port)`);
    });
    if (el.getAttribute('role') === 'button') {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
      });
    }
  });

  // ==================================================
  // SCRATCHPAD MODULE
  // ==================================================
  (function scratchpad() {
    const STORAGE_NOTES = 'apb-cc-notes';
    const STORAGE_POS = 'apb-cc-scratchpad-pos';
    const STORAGE_COLLAPSED = 'apb-cc-scratchpad-collapsed';

    const root = document.getElementById('scratchpad');
    const listEl = root.querySelector('.scratchpad-list');
    const inputEl = root.querySelector('.scratchpad-input');
    const addBtn = root.querySelector('.scratchpad-add');
    const collapseBtn = root.querySelector('.scratchpad-collapse');
    const fab = root.querySelector('.scratchpad-fab');
    const badge = root.querySelector('.scratchpad-fab .badge');
    const headEl = root.querySelector('.scratchpad-head');
    const countEl = root.querySelector('.note-count');

    let notes = [];
    try { notes = JSON.parse(localStorage.getItem(STORAGE_NOTES)) || []; } catch(e) {}

    function save() { try { localStorage.setItem(STORAGE_NOTES, JSON.stringify(notes)); } catch(e) {} }
    function esc(s) { return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }
    function render() {
      listEl.innerHTML = '';
      if (notes.length === 0) {
        const e = document.createElement('div');
        e.className = 'scratchpad-empty';
        e.textContent = 'No notes yet — quiet in here.';
        listEl.appendChild(e);
      } else {
        notes.forEach(n => {
          const noteEl = document.createElement('div');
          noteEl.className = 'note';
          noteEl.innerHTML = `<div class="note-body"><div class="note-text">${esc(n.text)}</div><div class="note-ts">${esc(n.ts)}</div></div><button class="note-del" aria-label="Delete note" title="Delete">×</button>`;
          noteEl.querySelector('.note-del').addEventListener('click', () => {
            notes = notes.filter(x => x.id !== n.id);
            save(); render();
          });
          listEl.appendChild(noteEl);
        });
      }
      badge.textContent = notes.length;
      badge.style.display = notes.length > 0 ? 'flex' : 'none';
      countEl.textContent = notes.length;
    }
    function addNote() {
      const text = inputEl.value.trim();
      if (!text) return;
      const now = new Date();
      const ts = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')} · ${days[now.getDay()]} ${String(now.getDate()).padStart(2,'0')} ${months[now.getMonth()]}`;
      notes.unshift({ id: now.getTime() + Math.floor(Math.random()*1000), text, ts });
      inputEl.value = '';
      save(); render();
    }
    addBtn.addEventListener('click', addNote);
    inputEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } });

    function applyPos(x, y) {
      root.style.left = x + 'px';
      root.style.top = y + 'px';
      root.style.right = 'auto';
      root.style.bottom = 'auto';
    }
    function ensureVisible() {
      requestAnimationFrame(() => {
        const rect = root.getBoundingClientRect();
        const pad = 16;
        let x = rect.left, y = rect.top;
        let changed = false;
        if (rect.right > window.innerWidth - pad)  { x = window.innerWidth  - rect.width  - pad; changed = true; }
        if (rect.bottom > window.innerHeight - pad) { y = window.innerHeight - rect.height - pad; changed = true; }
        if (x < pad) { x = pad; changed = true; }
        if (y < pad) { y = pad; changed = true; }
        if (changed) {
          applyPos(x, y);
          try { localStorage.setItem(STORAGE_POS, JSON.stringify({ x, y })); } catch(err) {}
        }
      });
    }

    function setCollapsed(c) {
      if (c) { root.classList.add('collapsed'); root.classList.remove('expanded'); }
      else { root.classList.remove('collapsed'); root.classList.add('expanded'); inputEl.focus(); ensureVisible(); }
      try { localStorage.setItem(STORAGE_COLLAPSED, c ? '1' : '0'); } catch(e) {}
    }
    setCollapsed(localStorage.getItem(STORAGE_COLLAPSED) !== '0');
    collapseBtn.addEventListener('click', () => setCollapsed(true));
    window.addEventListener('resize', () => { if (root.classList.contains('expanded')) ensureVisible(); });

    try {
      const p = JSON.parse(localStorage.getItem(STORAGE_POS));
      if (p && typeof p.x === 'number' && typeof p.y === 'number') applyPos(p.x, p.y);
    } catch(e) {}

    function makeDraggable(handle) {
      let state = null;
      handle.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button, input') && e.target !== handle) return;
        try { handle.setPointerCapture(e.pointerId); } catch(err) {}
        const rect = root.getBoundingClientRect();
        state = { offX: e.clientX - rect.left, offY: e.clientY - rect.top, moved: false, id: e.pointerId };
        handle.classList.add('dragging');
      });
      handle.addEventListener('pointermove', (e) => {
        if (!state || e.pointerId !== state.id) return;
        const dx = Math.abs(e.clientX - state.offX - root.getBoundingClientRect().left);
        const dy = Math.abs(e.clientY - state.offY - root.getBoundingClientRect().top);
        if (dx > 2 || dy > 2) state.moved = true;
        let x = e.clientX - state.offX, y = e.clientY - state.offY;
        const rect = root.getBoundingClientRect();
        x = Math.max(0, Math.min(window.innerWidth - rect.width, x));
        y = Math.max(0, Math.min(window.innerHeight - rect.height, y));
        applyPos(x, y);
      });
      handle.addEventListener('pointerup', (e) => {
        if (!state) return;
        handle.classList.remove('dragging');
        if (state.moved) {
          const rect = root.getBoundingClientRect();
          try { localStorage.setItem(STORAGE_POS, JSON.stringify({ x: rect.left, y: rect.top })); } catch(err) {}
          if (handle === fab) fab.dataset.justDragged = '1';
        }
        state = null;
      });
      handle.addEventListener('pointercancel', () => { state = null; handle.classList.remove('dragging'); });
    }
    makeDraggable(headEl);
    makeDraggable(fab);

    fab.addEventListener('click', () => {
      if (fab.dataset.justDragged) { delete fab.dataset.justDragged; return; }
      setCollapsed(false);
    });

    render();
  })();

  // ==================================================
  // LAYOUT PERSISTENCE + DRAG-DROP + CUSTOM PANELS + CUSTOM QUADRANTS + COLLAPSE
  // ==================================================
  (function layout() {
    const LK_QUAD = 'apb-cc-quad-order';
    const LK_PANEL = 'apb-cc-panel-order';
    const LK_CUSTOM = 'apb-cc-custom-panels';
    const LK_CUSTOM_QUADS = 'apb-cc-custom-quadrants';
    const LK_COLLAPSED = 'apb-cc-collapsed';
    const gridEl = document.querySelector('main.grid');
    const addTile = document.getElementById('add-quad-tile');

    function esc(s) { return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

    // ---- Custom panel + custom quadrant + collapse state ----
    let customPanels = [];
    let customQuadrants = [];
    let collapsedState = {};
    try { customPanels = JSON.parse(localStorage.getItem(LK_CUSTOM)) || []; } catch(e) {}
    try { customQuadrants = JSON.parse(localStorage.getItem(LK_CUSTOM_QUADS)) || []; } catch(e) {}
    try { collapsedState = JSON.parse(localStorage.getItem(LK_COLLAPSED)) || {}; } catch(e) {}
    function saveCustom()      { try { localStorage.setItem(LK_CUSTOM, JSON.stringify(customPanels)); } catch(e) {} }
    function saveCustomQuads() { try { localStorage.setItem(LK_CUSTOM_QUADS, JSON.stringify(customQuadrants)); } catch(e) {} }
    function saveCollapsed()   { try { localStorage.setItem(LK_COLLAPSED, JSON.stringify(collapsedState)); } catch(e) {} }

    function renderCustomPanel(data) {
      const el = document.createElement('div');
      el.className = 'panel custom-panel ' + (data.tint || '');
      el.dataset.panelId = data.id;
      const linkHtml = data.url ? `<a class="custom-link" href="${esc(data.url)}" target="_blank" rel="noopener">OPEN ↗</a>` : '';
      el.innerHTML = `
        <div class="panel-head">
          <div class="panel-title">${esc(data.title || 'UNTITLED')} <span class="mock">CUSTOM</span></div>
          <div class="panel-meta">
            <span class="custom-controls">
              <button class="custom-btn edit" title="Edit">EDIT</button>
              <button class="custom-btn del" title="Delete">DEL</button>
            </span>
          </div>
        </div>
        <div class="custom-content">${esc(data.content || '')}</div>
        ${linkHtml}
      `;
      el.querySelector('.custom-btn.edit').addEventListener('click', (e) => {
        e.stopPropagation();
        modal.open('edit', data);
      });
      el.querySelector('.custom-btn.del').addEventListener('click', (e) => {
        e.stopPropagation();
        if (confirm(`Delete "${data.title}"?`)) {
          customPanels = customPanels.filter(p => p.id !== data.id);
          saveCustom();
          el.remove();
          saveLayout();
        }
      });
      return el;
    }
    function addCustomToQuadrant(data) {
      const quad = document.getElementById(data.quadrantId);
      if (!quad) return;
      // If quadrant is collapsed, auto-expand so the new panel is visible
      if (quad.classList.contains('collapsed')) {
        quad.classList.remove('collapsed');
        if (collapsedState['quad:' + quad.id]) {
          delete collapsedState['quad:' + quad.id];
          saveCollapsed();
        }
      }
      quad.appendChild(renderCustomPanel(data));
    }

    // ---- Custom quadrant render ----
    function renderCustomQuadrant(data) {
      const section = document.createElement('section');
      section.className = 'quad custom-quad';
      section.id = data.id;
      const codeSafe = esc(data.code || 'CUSTOM');
      const nameSafe = esc(data.name || '');
      section.innerHTML = `
        <div class="quad-label">
          <span class="code">// ${codeSafe}</span>
          <span class="name">${nameSafe}</span>
          <span class="spacer"></span>
          <button class="cfg cfg-add" data-add-panel="${data.id}" title="Add custom panel">+ ADD</button>
          <button class="custom-quad-btn edit" title="Edit quadrant name">EDIT</button>
          <button class="custom-quad-btn del" title="Delete quadrant">DEL</button>
        </div>
      `;
      section.querySelector('.custom-quad-btn.edit').addEventListener('click', (e) => {
        e.stopPropagation();
        modal.open('quad-edit', data);
      });
      section.querySelector('.custom-quad-btn.del').addEventListener('click', (e) => {
        e.stopPropagation();
        const panelCount = section.querySelectorAll('.panel').length;
        const msg = panelCount > 0
          ? `Delete quadrant "${data.code}" AND its ${panelCount} panel${panelCount === 1 ? '' : 's'}?`
          : `Delete empty quadrant "${data.code}"?`;
        if (!confirm(msg)) return;
        const panelIds = [...section.querySelectorAll('.panel')].map(p => p.dataset.panelId).filter(Boolean);
        customPanels = customPanels.filter(p => !panelIds.includes(p.id));
        saveCustom();
        customQuadrants = customQuadrants.filter(q => q.id !== data.id);
        saveCustomQuads();
        // Clean up collapse state for this quad + its panels
        delete collapsedState['quad:' + data.id];
        panelIds.forEach(pid => delete collapsedState['panel:' + pid]);
        saveCollapsed();
        section.remove();
        saveLayout();
      });
      // Note: + ADD panel button uses gridEl event delegation (installed below), don't wire inline
      return section;
    }
    // Render custom quadrants BEFORE the add-tile (add-tile stays last via CSS order)
    customQuadrants.forEach(q => gridEl.insertBefore(renderCustomQuadrant(q), addTile));
    // Now render custom panels (some may belong to custom quadrants)
    customPanels.forEach(addCustomToQuadrant);

    // ---- Save current layout ----
    function saveLayout() {
      const quadOrder = [...gridEl.querySelectorAll(':scope > .quad')].map(q => q.id);
      const panelOrder = {};
      gridEl.querySelectorAll(':scope > .quad').forEach(q => {
        panelOrder[q.id] = [...q.querySelectorAll(':scope > .panel')].map(p => p.dataset.panelId).filter(Boolean);
      });
      try {
        localStorage.setItem(LK_QUAD, JSON.stringify(quadOrder));
        localStorage.setItem(LK_PANEL, JSON.stringify(panelOrder));
      } catch(e) {}
    }

    // ---- Restore layout ----
    (function restoreLayout() {
      try {
        const order = JSON.parse(localStorage.getItem(LK_QUAD));
        if (Array.isArray(order)) order.forEach(id => {
          const el = document.getElementById(id);
          if (el) gridEl.appendChild(el);
        });
      } catch(e) {}
      try {
        const orders = JSON.parse(localStorage.getItem(LK_PANEL));
        if (orders) Object.entries(orders).forEach(([qid, pids]) => {
          const quad = document.getElementById(qid);
          if (!quad) return;
          pids.forEach(pid => {
            const p = quad.querySelector(`[data-panel-id="${pid}"]`);
            if (p) quad.appendChild(p);
          });
        });
      } catch(e) {}
      // Force + ADD QUADRANT tile to DOM-last so new quadrants insertBefore it land at the bottom
      if (addTile) gridEl.appendChild(addTile);
    })();

    // ---- Sortable helper ----
    // Reorders direct children of `container` matching `itemSelector`
    // when the user grabs an element matching `handleSelector`.
    function makeSortable(container, handleSelector, itemSelector, onReorder) {
      container.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        const handle = e.target.closest(handleSelector);
        if (!handle || !container.contains(handle)) return;
        if (e.target.closest('a, button, input, textarea, select')) return;
        const item = handle.closest(itemSelector);
        if (!item || item.parentNode !== container) return;

        const startX = e.clientX, startY = e.clientY;
        let started = false;

        const move = (ev) => {
          if (!started) {
            if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < 5) return;
            started = true;
            item.classList.add('drag-source');
            document.body.classList.add('is-dragging');
          }
          const target = document.elementFromPoint(ev.clientX, ev.clientY);
          if (!target) return;
          const sibling = target.closest(itemSelector);
          if (!sibling || sibling === item || sibling.parentNode !== container) return;
          const rect = sibling.getBoundingClientRect();
          const cols = getComputedStyle(container).gridTemplateColumns.split(' ').length;
          const isHoriz = getComputedStyle(container).display.includes('grid') && cols > 1;
          const before = isHoriz
            ? ev.clientX < rect.left + rect.width / 2
            : ev.clientY < rect.top + rect.height / 2;
          if (before) container.insertBefore(item, sibling);
          else container.insertBefore(item, sibling.nextSibling);
        };
        const up = () => {
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', up);
          document.removeEventListener('pointercancel', up);
          if (started) {
            item.classList.remove('drag-source');
            document.body.classList.remove('is-dragging');
            onReorder && onReorder();
          }
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        document.addEventListener('pointercancel', up);
      });
    }

    // Init sortables:
    //   - Quadrant reordering: drag the .quad-label to swap quadrants
    //   - Panel reordering:    drag the .panel-head to reorder panels WITHIN a quadrant
    makeSortable(gridEl, '.quad-label', '.quad', saveLayout);
    gridEl.querySelectorAll(':scope > .quad').forEach(q => {
      makeSortable(q, '.panel-head', '.panel', saveLayout);
    });

    // ---- Modal ----
    // Modes: 'add' / 'edit' (panel) · 'quad-add' / 'quad-edit' (quadrant)
    // ('add' and 'edit' are kept as aliases for panel-mode to avoid breaking existing callers.)
    const modal = (function() {
      const overlay = document.getElementById('modal-overlay');
      const titleEl = document.getElementById('modal-title-field');
      const subtitleEl = document.getElementById('modal-subtitle-field');
      const contentEl = document.getElementById('modal-content-field');
      const urlEl = document.getElementById('modal-url-field');
      const headText = document.getElementById('modal-head-text');
      const saveBtn = document.getElementById('modal-save');
      const cancelBtn = document.getElementById('modal-cancel');
      const closeBtn = document.getElementById('modal-close');
      const deleteBtn = document.getElementById('modal-delete');
      const tintBtns = overlay.querySelectorAll('.tint-btn');
      let mode = 'add';
      let editing = null;

      function setActiveTint(tint) {
        tintBtns.forEach(b => b.classList.toggle('active', (b.dataset.tint || '') === (tint || '')));
      }
      function getActiveTint() {
        const active = overlay.querySelector('.tint-btn.active');
        return active ? (active.dataset.tint || '') : 'tint-ok';
      }
      tintBtns.forEach(b => b.addEventListener('click', () => setActiveTint(b.dataset.tint)));

      function open(m, data) {
        mode = m;
        editing = data;
        overlay.setAttribute('data-mode', m);
        if (m === 'quad-add') {
          headText.textContent = '◆ NEW QUADRANT';
          titleEl.placeholder = 'e.g. SUPPLIERS';
          titleEl.value = '';
          subtitleEl.value = '';
          deleteBtn.style.display = 'none';
        } else if (m === 'quad-edit') {
          headText.textContent = '◆ EDIT QUADRANT';
          titleEl.placeholder = 'e.g. SUPPLIERS';
          titleEl.value = data.code || '';
          subtitleEl.value = data.name || '';
          deleteBtn.style.display = 'inline-block';
        } else if (m === 'edit') {
          headText.textContent = '◆ EDIT PANEL';
          titleEl.placeholder = 'e.g. Waiting on financing';
          titleEl.value = data.title || '';
          contentEl.value = data.content || '';
          urlEl.value = data.url || '';
          setActiveTint(data.tint || 'tint-ok');
          deleteBtn.style.display = 'inline-block';
        } else {
          // 'add' or 'panel-add'
          headText.textContent = '◆ NEW PANEL · ' + (data.quadrantId || '').toUpperCase();
          titleEl.placeholder = 'e.g. Waiting on financing';
          titleEl.value = '';
          contentEl.value = '';
          urlEl.value = '';
          setActiveTint('tint-ok');
          deleteBtn.style.display = 'none';
        }
        overlay.classList.remove('hidden');
        setTimeout(() => titleEl.focus(), 60);
      }
      function close() { overlay.classList.add('hidden'); overlay.removeAttribute('data-mode'); }

      function save() {
        const title = titleEl.value.trim();
        if (!title) { titleEl.focus(); return; }

        // ---- Quadrant modes ----
        if (mode === 'quad-add') {
          const q = {
            id: 'quad-custom-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
            code: title.toUpperCase(),
            name: subtitleEl.value.trim().toUpperCase(),
            createdAt: Date.now()
          };
          customQuadrants.push(q);
          saveCustomQuads();
          const el = renderCustomQuadrant(q);
          gridEl.insertBefore(el, addTile);
          // Make its panel-drag sortable + inject collapse chevrons for the new quadrant
          makeSortable(el, '.panel-head', '.panel', saveLayout);
          injectCollapseButtons();
          saveLayout();
          close();
          return;
        }
        if (mode === 'quad-edit' && editing) {
          editing.code = title.toUpperCase();
          editing.name = subtitleEl.value.trim().toUpperCase();
          const el = document.getElementById(editing.id);
          if (el) {
            el.querySelector('.quad-label .code').textContent = '// ' + editing.code;
            el.querySelector('.quad-label .name').textContent = editing.name;
          }
          saveCustomQuads();
          close();
          return;
        }

        // ---- Panel modes ----
        const content = contentEl.value.trim();
        const url = urlEl.value.trim();
        const tint = getActiveTint();
        if (mode === 'edit' && editing) {
          editing.title = title;
          editing.content = content;
          editing.url = url;
          editing.tint = tint;
          const el = document.querySelector(`[data-panel-id="${editing.id}"]`);
          if (el) {
            el.className = 'panel custom-panel ' + tint;
            el.querySelector('.panel-title').innerHTML = `${esc(title)} <span class="mock">CUSTOM</span>`;
            el.querySelector('.custom-content').textContent = content;
            let link = el.querySelector('.custom-link');
            if (url) {
              if (!link) {
                link = document.createElement('a');
                link.className = 'custom-link';
                link.target = '_blank';
                link.rel = 'noopener';
                link.textContent = 'OPEN ↗';
                el.appendChild(link);
              }
              link.href = url;
            } else if (link) {
              link.remove();
            }
          }
          saveCustom();
        } else {
          const newData = {
            id: 'custom-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
            quadrantId: editing.quadrantId,
            title, content, url, tint,
            createdAt: Date.now()
          };
          customPanels.push(newData);
          saveCustom();
          addCustomToQuadrant(newData);
          injectCollapseButtons();
          saveLayout();
        }
        close();
      }

      function del() {
        if (!editing || !editing.id) return;
        // Quadrant delete is handled by the DEL button in the quadrant itself (with proper confirm text)
        if (mode === 'quad-edit') {
          const panelCount = document.querySelectorAll(`#${editing.id} .panel`).length;
          const msg = panelCount > 0
            ? `Delete quadrant "${editing.code}" AND its ${panelCount} panel${panelCount === 1 ? '' : 's'}?`
            : `Delete empty quadrant "${editing.code}"?`;
          if (!confirm(msg)) return;
          const el = document.getElementById(editing.id);
          if (el) {
            const panelIds = [...el.querySelectorAll('.panel')].map(p => p.dataset.panelId).filter(Boolean);
            customPanels = customPanels.filter(p => !panelIds.includes(p.id));
            saveCustom();
            panelIds.forEach(pid => delete collapsedState['panel:' + pid]);
          }
          customQuadrants = customQuadrants.filter(q => q.id !== editing.id);
          saveCustomQuads();
          delete collapsedState['quad:' + editing.id];
          saveCollapsed();
          if (el) el.remove();
          saveLayout();
          close();
          return;
        }
        // Panel delete
        if (!confirm(`Delete "${editing.title}"?`)) return;
        customPanels = customPanels.filter(p => p.id !== editing.id);
        saveCustom();
        delete collapsedState['panel:' + editing.id];
        saveCollapsed();
        const el = document.querySelector(`[data-panel-id="${editing.id}"]`);
        if (el) el.remove();
        saveLayout();
        close();
      }

      saveBtn.addEventListener('click', save);
      cancelBtn.addEventListener('click', close);
      closeBtn.addEventListener('click', close);
      deleteBtn.addEventListener('click', del);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay.classList.contains('hidden')) close();
      });
      titleEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } });

      return { open, close };
    })();

    // ---- + ADD PANEL: event delegation (covers built-in + dynamically-added custom quadrants) ----
    gridEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-panel]');
      if (!btn) return;
      e.stopPropagation();
      modal.open('add', { quadrantId: btn.dataset.addPanel });
    });

    // ---- + ADD QUADRANT tile ----
    if (addTile) {
      addTile.addEventListener('click', () => modal.open('quad-add', {}));
      addTile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); modal.open('quad-add', {}); }
      });
    }

    // ---- Make panel-drag sortable on restored custom quadrants (built-in were done above) ----
    document.querySelectorAll('.quad.custom-quad').forEach(q => {
      makeSortable(q, '.panel-head', '.panel', saveLayout);
    });

    // ==================================================
    // COLLAPSE / EXPAND — chevron on every panel-head + quad-label
    // ==================================================
    function injectCollapseButtons() {
      document.querySelectorAll('.panel-head').forEach(head => {
        if (head.querySelector('.collapse-btn')) return;
        const btn = document.createElement('button');
        btn.className = 'collapse-btn';
        btn.type = 'button';
        btn.innerHTML = '▾';
        btn.setAttribute('aria-label', 'Collapse / expand panel');
        btn.title = 'Collapse / expand';
        head.appendChild(btn);
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const panel = btn.closest('.panel');
          if (!panel) return;
          panel.classList.toggle('collapsed');
          const pid = panel.dataset.panelId;
          if (pid) {
            if (panel.classList.contains('collapsed')) collapsedState['panel:' + pid] = true;
            else delete collapsedState['panel:' + pid];
            saveCollapsed();
          }
        });
      });
      document.querySelectorAll('.quad-label').forEach(label => {
        if (label.querySelector('.collapse-btn')) return;
        const btn = document.createElement('button');
        btn.className = 'collapse-btn';
        btn.type = 'button';
        btn.innerHTML = '▾';
        btn.setAttribute('aria-label', 'Collapse / expand quadrant');
        btn.title = 'Collapse / expand';
        label.appendChild(btn);
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const quad = label.closest('.quad');
          if (!quad) return;
          quad.classList.toggle('collapsed');
          const qid = quad.id;
          if (qid) {
            if (quad.classList.contains('collapsed')) collapsedState['quad:' + qid] = true;
            else delete collapsedState['quad:' + qid];
            saveCollapsed();
          }
        });
      });
    }
    function restoreCollapsed() {
      Object.entries(collapsedState).forEach(([k, v]) => {
        if (!v) return;
        const idx = k.indexOf(':');
        if (idx < 0) return;
        const type = k.slice(0, idx);
        const id = k.slice(idx + 1);
        let el = null;
        if (type === 'panel') el = document.querySelector(`[data-panel-id="${id}"]`);
        else if (type === 'quad') el = document.getElementById(id);
        if (el) el.classList.add('collapsed');
      });
    }
    injectCollapseButtons();
    restoreCollapsed();
  })();

  // ==================================================
  // FONT SWITCHER — cycles A / B / C stacks, persists in localStorage
  // ==================================================
  (function fontSwitcher() {
    const KEY = 'apb-cc-font-stack';
    const stacks = [
      { key: 'A', label: 'TERMINAL' },
      { key: 'B', label: 'EDITORIAL' },
      { key: 'C', label: 'NEUTRAL' }
    ];
    const btn = document.querySelector('.font-switch');
    if (!btn) return;
    const curEl = btn.querySelector('.cur');
    let idx = 0;
    const saved = localStorage.getItem(KEY);
    if (saved) {
      const found = stacks.findIndex(s => s.key === saved);
      if (found >= 0) idx = found;
    }
    function apply() {
      const s = stacks[idx];
      document.body.setAttribute('data-font-stack', s.key);
      curEl.textContent = `${s.key} · ${s.label}`;
      try { localStorage.setItem(KEY, s.key); } catch(e) {}
    }
    apply();
    btn.addEventListener('click', () => {
      idx = (idx + 1) % stacks.length;
      apply();
    });
  })();

  // ==================================================
  // SIZE SWITCHER — A− / % / A+ scales dashboard UI zoom
  // Range: 85% → 125% in 5% steps · persists in localStorage
  // ==================================================
  (function sizeSwitcher() {
    const KEY = 'apb-cc-ui-zoom';
    const MIN = 0.85, MAX = 1.25, STEP = 0.05;
    const downBtn = document.querySelector('.size-down');
    const upBtn = document.querySelector('.size-up');
    const curEl = document.querySelector('.size-cur');
    if (!downBtn || !upBtn || !curEl) return;

    let zoom = 1;
    const saved = parseFloat(localStorage.getItem(KEY));
    if (!isNaN(saved) && saved >= MIN && saved <= MAX) zoom = saved;

    function apply() {
      document.body.style.setProperty('--ui-zoom', zoom.toString());
      curEl.textContent = Math.round(zoom * 100) + '%';
      downBtn.disabled = zoom <= MIN + 0.001;
      upBtn.disabled = zoom >= MAX - 0.001;
      try { localStorage.setItem(KEY, zoom.toString()); } catch(e) {}
    }
    apply();
    downBtn.addEventListener('click', () => {
      zoom = Math.max(MIN, Math.round((zoom - STEP) * 100) / 100);
      apply();
    });
    upBtn.addEventListener('click', () => {
      zoom = Math.min(MAX, Math.round((zoom + STEP) * 100) / 100);
      apply();
    });
  })();
